import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Divider, Alert, Tooltip, Chip } from "@mui/material";
import toast from "react-hot-toast";
import ExpertSelectionCard from "../components/ExpertSelectionCard";
import AddExpertSelectCard from "../components/AddExpertSelectCard";
import imagePlaceholder from "../../../assets/svg/image_placeholder.svg";
import ExpertGeneralInfo from "../components/ExpertGeneralInfo";
import IdealExpertExplanation from "../components/IdealExpertExplanation";
import { Breadcrumbs, buildProjectTrail } from "../../../components/Breadcrumbs";
import { ReactComponent as ProjectBriefIcon } from "../../../assets/svg/project-brief.svg";
import ExpertSelectionSwitcher from "../components/ExpertSelectionSwitcher";
import AddProjectModal from "../components/AddProjectModal";
import ExpertSection from "../components/ExpertSection";
import { ReopenSelectionNotice } from "../components/ReopenSelectionNotice";
import { Button } from "../../../components/Button/Button";
import { BriefView } from "../../../components/Brief/BriefView";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { getProjectPros, getBrandProject } from "../../../api/endpoints";
import { FilmWindowField } from "../../../components/FilmWindow/FilmWindowField";
import { useSelectionFilmWindow } from "../../../hooks/useSelectionFilmWindow";
import {
    canEditFilmWindow,
    hasFilmWindow,
    getFilmDays,
} from "../../../utils/filmWindow";
import { useProjectExpertScores } from "../../../hooks/useProjectExpertScores";
import { useProcessSiblings } from "../../../hooks/useProcessSiblings";
import { useSearchExpertScores } from "../../../hooks/useSearchExpertScores";
import axiosInstant from "../../../api";
import { useUser } from "../../../hooks/useUser";
import { UploadRegistryProvider, UploadRegistry, UploadKey } from "../../../hooks/useUploadRegistry";
import { isInternal, isExpert, EXPERT_FILTER_KEYS } from "../../../utils";
import { cldUrl } from "../../../utils/cloudinary";
import { BRIEF_GENDER_NO_PREFERENCE } from "../../pro/createEditProfile/formFragments/constants";
import {
    changeSelectionRoster,
    RosterAction,
    rosterErrorMessage,
} from "../../../utils/selectionRoster";
import { ProjectItemMenu } from "../components/ProjectItemMenu";
import { briefBlockedReason } from "../components/projectStatusActions";
import { DotLottleLoader, LoadMoreBtn } from "../../../components/Loader";
import { BrandProject } from "../components/constants";
import {
    BRAND_PROJECT_STATUSES,
    updateExpertScoreInProjectPros,
    updateExpertRateInProjectPros,
    calculateMaxPercentage,
    sortExpertsBySelection,
    sortExpertsByScore,
    getAssociationIds,
    getVideoCounts,
    labelChannelEntries,
    describeSharedSiblings,
    PILL_SX,
    CHANNEL_PILL_SX,
    getExpertCounts,
    getModalType,
    getBpp,
} from "../helper";
import { capitalize } from "lodash";
import { useProfessions } from "../../../hooks/useProfessions";
import { EmptyPanel } from "../../brand/projects/common/EmptyPanel";
import eventEmitter from "../../../utils/eventEmitter";
import { BriefDialog } from "../../../components/Brief/BriefDialog";
import { Downloads } from "../components/downloads";
import { Pro } from "../components/ExpertSelectedCard";
import ExpertSelectionRecommendationDialog from "./ExpertSelectionRecommendationDialog";
import { PageTitle } from "../../../components/PageTitle";
import ExpertRanking from "../components/ExpertRanking";

import OptInNotificationsModal from "../components/OptInNotificationsModal";
import AnimatedReorderItem from "../components/AnimatedReorderItem";
import { ProductDetailsCard, KeyProjectDatesCard, ProjectTimelineCard, ProjectPricingCard } from "../components/ProjectDetailsSidebar";
import ActivityFeed from "../../../components/ActivityFeed";
import { formatCurrency } from "../../../utils/formatCurrency";
import { PriceBreakdownTooltip } from "../../../components/Pricing/PriceBreakdownTooltip";
import BrandInlineList from "../../../components/brand/BrandInlineList";

type TabKey = "details" | "expertProposals" | "expertRanking" | "video" | "downloads";

const tabKeyToIndex: Record<TabKey, string> = {
    details: "details",
    expertProposals: "expertProposals",
    expertRanking: "expertRanking",
    video: "video",
    downloads: "downloads"
};

const resolveTabKeyFromIndex = (index: any, role: any, projectStatus?: string): TabKey => {
    switch (index) {
        case "details":
            return "details";
        case "expertProposals":
            return isInternal(role) ? "expertProposals" : "details";
        case "expertRanking":
            return "expertRanking";
        case "video":
            return "downloads";
        case "downloads":
            return "downloads";
        default:
            return "details";
    }
};

const ExpertSelection = () => {
    const { projectId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { role } = useUser();
    const tabParam = searchParams.get("tab");

    const [project, setProject] = useState<BrandProject>();
    const [isProjectLoading, setIsProjectLoading] = useState(true);
    const [isActive, setIsActive] = useState<TabKey>(resolveTabKeyFromIndex(tabParam, role, project?.status));

    const [expertScores, setExpertScores] = useState<Record<string, number>>({});

    const brandId = project?.brands?.filter((brand: any) => brand.rmn !== true)?.[0]?.id;

    // Experts live on the project's selection process (brand_order_pros), which
    // can span several same-product projects in an order. When it does, casting
    // actions here are shared with the sibling projects — surfaced via a banner
    // and used to fan lifecycle transitions out (see handleSubmitForReview).
    const sharedProcess = useProcessSiblings(
        project?.order_id != null ? Number(project.order_id) : undefined,
        project?.id,
        { siblingCount: project?.process_project_count }
    );
    const otherProcessProjects = useMemo(
        () =>
            sharedProcess.siblingProjects.filter(
                (p) => Number(p.id) !== Number(project?.id)
            ),
        [sharedProcess.siblingProjects, project?.id]
    );

    const optedInExpertCount = useMemo(
        () => (project ? getExpertCounts(project as any).optedIn : 0),
        [project],
    );

    const selectionId =
        sharedProcess.processId != null
            ? sharedProcess.processId
            : (project as any)?.brand_order_pros_id ?? null;
    const {
        filmWindow,
        setFilmWindow,
        refetch: refetchFilmWindow,
    } = useSelectionFilmWindow(
        project?.order_id != null ? Number(project.order_id) : null,
        selectionId
    );

    // Opt-in/opt-out pitching opens when casting begins ('Expert Proposals')
    // and stays open through 'Scheduling' — roster approval releases experts
    // to the client and does not gate pitching. Mirrors the API's
    // PITCH_ELIGIBLE_STATUSES gate (409 ProjectNotPitchEligible) and the
    // daily follow-up cron's window.
    const isPitchEligible = [
        BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED,
        BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED,
        BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED,
        BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED,
        BRAND_PROJECT_STATUSES.SCHEDULING
    ].includes(project?.status || "");


    const [pros, setPros] = useState<Pro[]>([]);
    const [projectPros, setProjectPros] = useState<Pro[]>([]);
    const [projectProsLoading, setProjectProsLoading] = useState(false);

    // Experts whose debounced rate save hasn't settled yet. The opt-in send
    // is held while this is non-empty so the API's rate gate can't race a
    // rate the server hasn't received.
    const [pendingRateSaveIds, setPendingRateSaveIds] = useState<Set<number>>(
        () => new Set()
    );
    const handleRateSavingChange = (userId: number, saving: boolean) => {
        setPendingRateSaveIds((prev) => {
            if (prev.has(userId) === saving) return prev;
            const next = new Set(prev);
            if (saving) next.add(userId);
            else next.delete(userId);
            return next;
        });
    };

    // Why the opt-in send is unavailable, or "" when it is available — one
    // source for both the disabled state and the tooltip, so no disable
    // reason can go unexplained (all four previously greyed the button, two
    // of them with no message at all). Ordered most-actionable first.
    const optInDisabledReason = (() => {
        if (projectPros.length === 0) return "Add experts to this project first.";
        if ((project as any)?.process_locked) {
            return "This selection is committed — experts can no longer be pitched.";
        }
        if (!isPitchEligible) {
            return "Experts can be pitched once the project reaches Expert Proposals.";
        }
        if (!hasFilmWindow(filmWindow)) return "Set the filming date range first.";
        // Mirrors the API's ExpertRatesRequired gate: every proposal on the
        // roster needs a usable (> 0) rate before any pitch goes out.
        if (projectPros.some((p: any) => !(Number(getBpp(p)?.rate) > 0))) {
            return "Set a rate for every proposed expert before sending opt-in notifications.";
        }
        if (pendingRateSaveIds.size > 0) return "Saving expert rates…";
        return "";
    })();

    const submitDisabledReason = (() => {
        if (projectPros.length === 0) return "Add experts to this project first.";
        if ((project as any)?.process_locked) {
            return "This selection is committed and can no longer be submitted.";
        }
        if (project?.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED) {
            return "This project is already delivered.";
        }
        if (project?.status === BRAND_PROJECT_STATUSES.CANCELLED) {
            return "This project is cancelled.";
        }
        // Casting starts after the brief: submitting from a pre-brief status
        // would jump the project straight into proposal review (mirrors the
        // API's casting-floor rule on roster adds).
        if (
            [
                BRAND_PROJECT_STATUSES.PENDING,
                BRAND_PROJECT_STATUSES.BRIEF_PENDING,
                BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED,
                BRAND_PROJECT_STATUSES.BRIEF_DENIED,
            ].includes(project?.status || "")
        ) {
            return "The brief needs to be approved before experts can be submitted for review.";
        }
        // Once the proposals are approved (Experts Selection and beyond)
        // there is nothing to submit — adding a new expert moves the whole
        // selection back to Expert Proposals, which re-enables this button.
        if (
            [
                BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED,
                BRAND_PROJECT_STATUSES.SCHEDULING,
                BRAND_PROJECT_STATUSES.PRE_PRODUCTION,
                BRAND_PROJECT_STATUSES.FILMING,
                BRAND_PROJECT_STATUSES.EDITING,
                BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED,
            ].includes(project?.status || "")
        ) {
            return "Experts are already approved — adding a new expert reopens Expert Proposals.";
        }
        if (!hasFilmWindow(filmWindow)) {
            return "Set the filming date range before submitting these experts for review.";
        }
        if (
            projectPros.some(
                (expert) =>
                    expertScores[expert.userid] === undefined
                    || expertScores[expert.userid] === null
                    || Number.isNaN(expertScores[expert.userid]),
            )
        ) {
            return "Score every expert before submitting for review.";
        }
        return "";
    })();

    const [pendingReopen, setPendingReopen] = useState<{
        userid: number;
        displayName: string;
        action: RosterAction;
    } | null>(null);
    const [reopening, setReopening] = useState(false);

    const handleReopenConfirm = async () => {
        if (!pendingReopen) return;
        const effectiveBrandId =
            (project as any)?.brand_id ||
            project?.brands?.find((b: any) => b.rmn !== true)?.id;
        if (!effectiveBrandId || !project?.id) {
            toast.error("Couldn't resolve this project's brand. Refresh and try again.");
            return;
        }
        setReopening(true);
        try {
            await changeSelectionRoster(
                pendingReopen.action,
                effectiveBrandId,
                project.id,
                pendingReopen.userid,
                true
            );
            toast.success(
                pendingReopen.action === "add"
                    ? "Expert added. The selection is back in Expert Proposals."
                    : "Expert removed. The selection is back in Scheduling."
            );
            window.location.reload();
        } catch (error: any) {
            toast.error(rosterErrorMessage(error));
            setReopening(false);
            setPendingReopen(null);
        }
    };

    const [proCount, setProCount] = useState(0);
    const [projectProsCount, setProjectProCount] = useState(0);
    const [limit, setLimit] = useState(20);
    const [loading, setLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [filters, setFilters] = useState({});
    const [searchText, setSearchText] = useState("");

    // Algorithm-suggested match scores for every pro × every product in the
    // project (internal only). Declared up here (before the search-pool fetch)
    // so the pool can be sorted by the per-product algo score SERVER-side.
    const {
        products: algoProducts,
        scoresByUserId: algoScoresByUserId,
        breakdownByUserId: algoBreakdownByUserId,
        industryScoreByUserId: algoIndustryScoreByUserId,
        expertTypeByUserId: algoExpertTypeByUserId,
        professionsByUserId: algoProfessionsByUserId,
        productIdealsByDesireId: algoProductIdealsByDesireId,
        refetch: refetchAlgoScores,
    } = useProjectExpertScores(project?.id ? Number(project.id) : undefined);

    // The project's product (desire) ids that carry algo scores; drives the
    // server-side "best match" sort. Stable key so the pool fetch re-runs once
    // scores load.
    const scoreDesireIdsKey = useMemo(
        () =>
            (algoProducts || [])
                .map((p: any) => p.desireid)
                .filter(Boolean)
                .sort((a: number, b: number) => a - b)
                .join(","),
        [algoProducts]
    );

    // Default the Expert Proposals pool to "best match" (algo score) sorting for
    // internal users; the toggle in ExpertSection flips this off.
    const [sortByScore, setSortByScore] = useState(true);

    const prosCacheRef = React.useRef<{
        params: string;
        data: Pro[];
        count: number;
    } | null>(null);

    const isFetchingProsRef = React.useRef(false);
    const fetchedProjectIdRef = React.useRef<number | null>(null);

    const [showBriefDialog, setShowBriefDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showOptInModal, setShowOptInModal] = useState(false);
    const [modalType, setModalType] = useState<"firstTime" | "alreadySubmitted">("firstTime");
    const [hasInitializedFilters, setHasInitializedFilters] = useState(false);
    const [isInitializingFilters, setIsInitializingFilters] = useState(false);
    const [hasEverClearedFilters, setHasEverClearedFilters] = useState(false);

    const { professionsList, subProfessionLibrary, getProfessions, areProfessionsLoaded } = useProfessions();

    useEffect(() => {
        const newActiveTab = resolveTabKeyFromIndex(tabParam, role, project?.status);
        if (newActiveTab !== isActive) {
            setIsActive(newActiveTab);
        }
    }, [project?.status, tabParam, role, isActive]);

    const getExpertRank = (expert: any): number => expert?.projects?.[0]?.BrandProjectPro?.status || 0;

    const getExpertWithHighestRank = (experts: any[]): any => {
        if (!experts?.length) return null;

        const expertsWithRanks = experts.filter((expert) => getExpertRank(expert) > 0);

        if (expertsWithRanks.length === 0) return null;

        const topExpert = expertsWithRanks.reduce((highest: any, current: any) => {
            const currentRank = getExpertRank(current);
            const highestRank = getExpertRank(highest);
            return currentRank < highestRank ? current : highest;
        });

        return topExpert;
    };

    const getTopExpertDisplay = (experts: any[]) => {
        const topExpert = getExpertWithHighestRank(experts);
        if (!topExpert) return null;

        return (
            <>
                <div className="hidden sm:block text-[14px] font-[450] text-text-2">|</div>
                <div className="flex items-center gap-2">
                    {topExpert?.avatar && (
                        <img
                            className="w-[24px] h-[24px] object-cover object-top rounded-[6px]"
                            src={cldUrl(topExpert.avatar, { w: 48, h: 48, crop: "fill", gravity: "face" })}
                            alt="Expert"
                        />
                    )}
                    <div className="text-[14px] font-[450] text-text-2">{topExpert?.displayName?.split(" ")[0]}</div>
                </div>
            </>
        );
    };

    useEffect(() => {
        if (!areProfessionsLoaded) {
            getProfessions();
        }
    }, []);

    const handleTabChange = (tabKey: TabKey) => {
        setIsActive(tabKey);
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.set("tab", tabKeyToIndex[tabKey] || "details");
        setSearchParams(newParams);
    };

    const updateExpertScore = (userId: string, score: number) => {
        setExpertScores((prev) => ({ ...prev, [userId]: score }));
        setProjectPros((prevPros) => updateExpertScoreInProjectPros(prevPros, userId, score));
    };

    const updateExpertRate = (userId: string | number, rate: string | null) => {
        setProjectPros((prevPros) => updateExpertRateInProjectPros(prevPros, userId, rate));
    };

    const [sortType] = useState({
        isAscending: false,
        key: "dateCreated"
    });

    // When "best match" is on (default) and the requester is internal, sort the
    // pool by the server-computed algorithm score (highest match across the
    // project's products); otherwise fall back to sortType (recency).
    const sortCommand = useMemo(() => {
        if (sortByScore && isInternal(role)) return "-algoScore";
        const { isAscending, key } = sortType;
        return `${isAscending ? "" : "-"}${key}`;
    }, [sortByScore, role, sortType]);

    const fetchProjectPros = async () => {
        if (!project?.id || !brandId) {
            return [];
        }

        const url = getProjectPros(brandId, project.id);
        try {
            setProjectProsLoading(true);
            const res = await axiosInstant.get(url);
            const data = res.data.rows || [];
            setProjectPros(data);
            setProjectProCount(data.length || 0);
            return data;
        } catch (err) {
            console.error("Failed to fetch project pros:", err);
            setProjectPros([]);
            return [];
        } finally {
            setProjectProsLoading(false);
        }
    };

    const getSearchedPros = async (projectProsOverride?: Pro[]) => {
        const activeProjectPros = projectProsOverride ?? projectPros ?? [];

        const params: any = {
            limit,
            sort: sortCommand
        };

        Object.keys(filters).forEach((key) => {
            const filterValue = (filters as any)[key];
            if (key !== "tab" && filterValue !== undefined && filterValue !== null && filterValue !== "") {
                params[key] = filterValue;
            }
        });

        if (searchText) {
            params.search = searchText;
        }

        // For the algo "best match" sort, tell the backend which products to
        // score against (the project's desire ids). It orders by MAX(score)
        // across them — highest for multiple products, not the average.
        // The Industry/Use-Case filter needs the same ids: the backend
        // classifies experts against these products before paginating.
        if (
            sortCommand === "-algoScore" ||
            sortCommand === "algoScore" ||
            (filters as any).expertType
        ) {
            if (scoreDesireIdsKey) params.scoreDesireIds = scoreDesireIdsKey;
        }

        const cacheKey = JSON.stringify(params);

        if (prosCacheRef.current && prosCacheRef.current.params === cacheKey) {
            const filteredPros = prosCacheRef.current.data
                .map((pro: any) => ({
                    ...pro,
                    selected: activeProjectPros.some((p: any) => p.userid === pro.userid)
                }))
                .filter((pro: any) => !activeProjectPros.some((p: any) => p.userid === pro.userid));

            setPros(filteredPros);
            setProCount(prosCacheRef.current.count || filteredPros.length);
            return;
        }

        if (isFetchingProsRef.current) {
            return;
        }

        try {
            isFetchingProsRef.current = true;
            if (pros.length === 0) setLoading(true);
            else setIsLoadingMore(true);

            // Use brand-specific experts endpoint for brand users, general endpoint for internal users
            let endpoint = "/users/pros";
            if (
                brandId &&
                role !== "ADMIN" &&
                role !== "PRO_TEAM_ADMIN" &&
                role !== "ACCOUNT_MANAGER_ADMIN" &&
                role !== "SALES_TEAM" &&
                role !== "POST_PRODUCTION_ADMIN"
            ) {
                endpoint = `/brands/${brandId}/pros`;
            }

            const response = await axiosInstant.get(endpoint, { params });
            const fetchedPros = response.data.rows || [];

            prosCacheRef.current = {
                params: cacheKey,
                data: fetchedPros,
                count: response.data.count || fetchedPros.length
            };

            const filteredPros = fetchedPros
                .map((pro: any) => ({
                    ...pro,
                    selected: activeProjectPros.some((p: any) => p.userid === pro.userid)
                }))
                .filter((pro: any) => !activeProjectPros.some((p: any) => p.userid === pro.userid));

            setPros(filteredPros);
            setProCount(response.data.count || filteredPros.length);
        } catch (error: any) {
            console.error("Error fetching pros:", error.response);
            setPros([]);
            setProCount(0);
        } finally {
            setLoading(false);
            setIsLoadingMore(false);
            isFetchingProsRef.current = false;
        }
    };

    const refreshAllData = async () => {
        const freshProjectPros = await fetchProjectPros();
        // Re-pull the per-product algo scores for the new roster so a just-added
        // expert's pill shows its (already-computed) score instead of "—".
        refetchAlgoScores();
        // A roster change can move the project server-side (a new proposal
        // rolls the whole selection back to Expert Proposals), so re-pull the
        // status fields too — quietly, no full-page loader — or the
        // Submit For Review / opt-in gating keeps reading a stale status.
        refreshProjectStatusSilently();
        setLimit(20);
        await getSearchedPros(freshProjectPros);
    };

    const handleLoadMore = () => {
        setLimit((prev) => prev + 20);
    };

    const handleSubmitForReview = async () => {
        if (!brandId || !project?.id) {
            toast.error("Project data missing");
            return;
        }

        const hasMissingScores = projectPros.some(
            (expert) =>
                expertScores[expert.userid] === undefined ||
                expertScores[expert.userid] === null ||
                Number.isNaN(expertScores[expert.userid])
        );

        if (hasMissingScores) {
            toast.error("Please input scores for all selected experts before submitting.");
            return;
        }

        try {
            // The API fans the shared submit-for-review transition out to the
            // sibling projects on this process by itself (source notifies,
            // siblings silent, never regressing one that's ahead).
            await axiosInstant.patch(`/brands/${brandId}/projects/${project.id}`, {
                status: BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED
            });
            const response = await axiosInstant.get(getBrandProject(project.id));
            setProject(response.data);

            // Success is a toast, not a modal — the user stays on the page
            // (the refreshed project above updates the status chip and the
            // button gating in place).
            toast.success("Experts submitted for review!");
            setShowConfirmDialog(false);
        } catch (error: any) {
            toast.error(
                error?.response?.data?.error === "FilmWindowRequired"
                    ? "Set the filming date range before submitting these experts for review."
                    : "Failed to submit experts. Please try again."
            );
            setShowConfirmDialog(false);
        }
    };

    const handleSubmitButtonClick = () => {
        if (project?.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED) {
            return;
        }

        setModalType(getModalType(project?.status));
        setShowConfirmDialog(true);
    };

    const uploadingKeysRef = React.useRef<Set<UploadKey>>(new Set());
    const projectRef = React.useRef<BrandProject | undefined>(undefined);
    useEffect(() => {
        projectRef.current = project;
    }, [project]);

    const uploadRegistry = useMemo<UploadRegistry>(
        () => ({
            add: (key) => uploadingKeysRef.current.add(key),
            remove: (key) => uploadingKeysRef.current.delete(key),
            has: (key) => uploadingKeysRef.current.has(key),
        }),
        [],
    );

    // Quiet, granular refresh: fetch the project but only patch projectFiles
    // (and channels) into existing state — no full-page loader, no whole-project
    // swap, and never overwrite a slot with an upload in flight. Used by both the
    // importReload socket event and the Downloads-tab post-upload callback so the
    // page never flashes, loses scroll, or drops an in-progress upload.
    const refreshProjectFilesSilently = () => {
        if (!projectId) return;
        const projectIdNum = Number(projectId);
        axiosInstant
            .get(getBrandProject(projectIdNum))
            .then((response) => {
                const incoming = response.data;
                setProject((prev) => {
                    if (!prev) return incoming;
                    const skip = uploadingKeysRef.current;
                    const prevFiles: any[] = (prev as any).projectFiles || [];
                    const prevById = new Map(prevFiles.map((f: any) => [f?.id ?? f?.hash, f]));
                    const mergedFiles = ((incoming?.projectFiles as any[]) || []).map((f: any) => {
                        const k = f?.id ?? f?.hash;
                        return skip.has(k) && prevById.has(k) ? prevById.get(k) : f;
                    });
                    return {
                        ...prev,
                        projectFiles: mergedFiles,
                        content_channels:
                            incoming?.content_channels ?? (prev as any).content_channels,
                    } as BrandProject;
                });
            })
            .catch(() => {});
    };

    // Quiet status refresh: re-pull the project but only patch the
    // status-bearing fields into existing state — no full-page loader, no
    // whole-project swap (same reasoning as refreshProjectFilesSilently).
    const refreshProjectStatusSilently = () => {
        if (!projectId) return;
        axiosInstant
            .get(getBrandProject(Number(projectId)))
            .then((response) => {
                const incoming = response.data;
                setProject((prev) => {
                    if (!prev) return incoming;
                    return {
                        ...prev,
                        status: incoming?.status ?? prev.status,
                        proposals_review_status:
                            incoming?.proposals_review_status ?? null,
                        process_locked:
                            incoming?.process_locked ??
                            (prev as any).process_locked,
                    } as BrandProject;
                });
            })
            .catch(() => {});
    };

    const fetchProject = (force = false) => {
        if (!projectId) return;

        const projectIdNum = Number(projectId);
        if (!force && fetchedProjectIdRef.current === projectIdNum && !isProjectLoading) {
            return;
        }

        fetchedProjectIdRef.current = projectIdNum;
        setIsProjectLoading(true);
        axiosInstant
            .get(getBrandProject(projectIdNum))
            .then((response) => {
                setProject(response.data);
            })
            .catch(() => {})
            .finally(() => {
                setIsProjectLoading(false);
            });
    };

    useEffect(() => {
        if (!tabParam) {
            const newParams = new URLSearchParams(searchParams.toString());
            newParams.set("tab", "1");
            setSearchParams(newParams);
        }
    }, [tabParam, searchParams, setSearchParams]);

    // Brief-note notification deep link (?briefNote=<noteId>): open the brief
    // dialog on its notes with that note focused. Internal viewers only (the
    // notes are internal; for anyone else the param is just dropped), and the
    // same gate as every other door into the brief dialog — a cancelled or
    // delivered project's brief stays shut.
    //
    // The param is consumed once: copied into state with the route's project
    // id and dropped from the URL at once (replace), before the filter-init
    // effect below pushes a new history entry that would carry it — so Back
    // never reopens the dialog.
    const briefNoteParam = searchParams.get("briefNote");
    const [briefNoteLink, setBriefNoteLink] = useState<{ projectId: string; noteId: string } | null>(null);
    useEffect(() => {
        if (!briefNoteParam) {
            // Moved to another project: drop a link that never opened.
            setBriefNoteLink((link) => (link && link.projectId !== projectId ? null : link));
            return;
        }
        // Keep the param until the user (and so the role) has loaded.
        if (!role) return;
        if (projectId && isInternal(role)) setBriefNoteLink({ projectId, noteId: briefNoteParam });
        setSearchParams(
            (p) => {
                p.delete("briefNote");
                return p;
            },
            { replace: true }
        );
    }, [briefNoteParam, projectId, role]);

    // On in-page navigation from another project, `project` still holds the
    // old one until the fetch lands: wait for the linked project, so the
    // dialog never opens (or is blocked) with the wrong project's brief.
    const briefNoteProjectReady =
        !!briefNoteLink && !!project && String(project.id) === String(briefNoteLink.projectId);
    const briefNoteBlocked = briefBlockedReason(project);
    const closeBriefNoteLink = () => setBriefNoteLink(null);

    useEffect(() => {
        if (!briefNoteProjectReady || !briefNoteBlocked) return;
        toast.error(briefNoteBlocked);
        closeBriefNoteLink();
    }, [briefNoteProjectReady, briefNoteBlocked]);

    useEffect(() => {
        if (!hasEverClearedFilters) {
            const urlFilters: any = {};
            EXPERT_FILTER_KEYS.forEach((key: string) => {
                const value = searchParams.get(key);
                if (value) urlFilters[key] = value;
            });

            if (Object.keys(urlFilters).length > 0) {
                setIsInitializingFilters(true);
                setFilters(urlFilters);
                setIsInitializingFilters(false);
            }
        }
    }, [searchParams, hasEverClearedFilters]);

    useEffect(() => {
        if (projectId) {
            fetchProject();
        }
    }, [projectId]);

    useEffect(() => {
        const handleImportReload = (payload: any) => {
            const incomingHash = payload?.hash;
            if (!incomingHash) return;
            const files: any[] = (projectRef.current as any)?.projectFiles || [];
            if (files.some((f) => f?.hash === incomingHash)) {
                refreshProjectFilesSilently();
            }
        };
        eventEmitter.on("importReload", handleImportReload);
        return () => {
            eventEmitter.off("importReload", handleImportReload);
        };
        // Subscribe once; the handler reads the latest project via projectRef and
        // refreshes granularly instead of a full-page reload. Avoids re-subscribing
        // on every projectFiles change.
    }, []);

    useEffect(() => {
        fetchProjectPros();
    }, [project?.id, brandId]);

    useEffect(() => {
        const initialScores: Record<string, number> = {};
        projectPros.forEach((expert) => {
            const score = expert.projects?.[0]?.BrandProjectPro?.score;
            if (score !== undefined && score !== null && !Number.isNaN(parseFloat(score))) {
                initialScores[expert.userid] = parseFloat(score);
            }
        });
        setExpertScores(initialScores);
    }, [projectPros]);

    useEffect(() => {
        if (project?.briefs?.[0] && !hasInitializedFilters) {
            setIsInitializingFilters(true);
            const brief = project.briefs[0];
            const currentFilters: any = { ...filters }; // Start with existing filters

            if (
                brief.expert_gender &&
                brief.expert_gender !== BRIEF_GENDER_NO_PREFERENCE &&
                !currentFilters.gender
            ) {
                currentFilters.gender = brief.expert_gender;
            }

            // Handle age range (only if not already set)
            if (brief.expert_age_min !== undefined && brief.expert_age_max !== undefined && !currentFilters.agerange) {
                currentFilters.agerange = `${brief.expert_age_min},${brief.expert_age_max}`;
            }

            // The brand's expert-type intent (industry / use_case) pre-sets the
            // Expert Type filter as a SOFT default — internal users can clear
            // or flip it from the Filters modal.
            if (brief.expert_type && brief.expert_type !== "either" && !currentFilters.expertType) {
                currentFilters.expertType = brief.expert_type;
            }

            if (brief.professions && brief.professions.length > 0) {
                // Extract unique profession IDs
                const professionIds = [
                    ...new Set(
                        brief.professions.map((p) => p.profession_id).filter((id) => id !== null && id !== undefined)
                    )
                ];

                if (professionIds.length > 0) {
                    currentFilters.profession = professionIds.join(",");
                }

                // Extract unique sub-profession IDs
                const subProfessionIds = [
                    ...new Set(
                        brief.professions
                            .map((p) => p.sub_profession_id)
                            .filter((id) => id !== null && id !== undefined)
                    )
                ];

                if (subProfessionIds.length > 0) {
                    currentFilters.subprofession = subProfessionIds.join(",");
                }
            } else {
                if (brief.profession_id && !currentFilters.profession) {
                    currentFilters.profession = brief.profession_id.toString();
                }
                if (brief.sub_profession_id && !currentFilters.subprofession) {
                    currentFilters.subprofession = brief.sub_profession_id.toString();
                }
            }

            // Update URL parameters
            const newParams = new URLSearchParams(searchParams.toString());
            Object.entries(currentFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    newParams.set(key, value.toString());
                }
            });
            setSearchParams(newParams);

            setFilters(currentFilters);
            setHasInitializedFilters(true);
            setIsInitializingFilters(false);
        } else if (project && !project.briefs?.[0] && !hasInitializedFilters) {
            setHasInitializedFilters(true);
        }
    }, [project, hasInitializedFilters, searchParams, setSearchParams, filters]);

    useEffect(() => {
        if (!hasInitializedFilters || !brandId || !project?.id || isActive !== "expertProposals") {
            return;
        }

        getSearchedPros();
    }, [limit, sortCommand, filters, searchText, hasInitializedFilters, brandId, project?.id, isActive, scoreDesireIdsKey]);

    const getProfessionNames = (brief: any) => {
        if (!brief) return "N/A";

        if (brief.professions && brief.professions.length > 0) {
            const professionNames = brief.professions
                .map((prof: any) => {
                    const profession = professionsList.find((p) => p.id === prof.profession_id);
                    const professionName = profession?.name || "Unknown Profession";

                    // If there's a sub-profession, include it
                    if (prof.sub_profession_id) {
                        const subProfession = subProfessionLibrary[prof.profession_id]?.find(
                            (sp: any) => sp.id === prof.sub_profession_id
                        );
                        return subProfession?.name || professionName;
                    }

                    return professionName;
                })
                .filter(Boolean);

            return professionNames.length > 0 ? professionNames.join(", ") : "N/A";
        }

        if (brief.profession_id) {
            const profession = professionsList.find((p) => p.id === brief.profession_id);
            const professionName = profession?.name || "Unknown Profession";

            // If there's a sub-profession, include it
            if (brief.sub_profession_id) {
                const subProfession = subProfessionLibrary[brief.profession_id]?.find(
                    (sp: any) => sp.id === brief.sub_profession_id
                );
                return subProfession?.name || professionName;
            }

            return professionName;
        }

        return "N/A";
    };

    const maxPercentage = useMemo(() => calculateMaxPercentage(projectPros), [projectPros]);

    // (algo scores hoisted to the top of the component — see useProjectExpertScores above)

    // Per-product scores for the SEARCH pool (experts not yet on the project) —
    // only fetched on the Expert Proposals tab for the currently-visible results.
    const searchUserIds = useMemo(
        () => pros.map((p: any) => p.userid).filter(Boolean),
        [pros]
    );
    const {
        scoresByUserId: searchScoresByUserId,
        expertTypesByUserId: searchExpertTypesByUserId,
    } = useSearchExpertScores(
        project?.id ? Number(project.id) : undefined,
        searchUserIds,
        { skip: isActive !== "expertProposals" }
    );

    const sortedProjectPros = useMemo(() => {
        if (isActive === "expertProposals") {
            return sortExpertsByScore(projectPros);
        } 
            return sortExpertsBySelection(projectPros);
        
    }, [projectPros, isActive]);

    if (!project && !isProjectLoading) {
        return null;
    }

    const associationIds = getAssociationIds(project);

    const handleIdClick = () => (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        navigator.clipboard.writeText(associationIds);
        toast.success("ID copied to clipboard");
    };

    // const handleIdClick = () => (e: React.MouseEvent<HTMLDivElement>) => {
    //     e.stopPropagation();
    //     navigator.clipboard.writeText(formatProjectId(project));
    //     toast.success("ID copied to clipboard");
    // };
    return (
        <div className="flex flex-col gap-[24px] pt-[40px]">
            <div className="flex items-start justify-between w-full flex-wrap gap-2">
                <div className="flex flex-col gap-2 px-[24px] min-w-0">
                    {project && (
                        <Breadcrumbs items={buildProjectTrail(project)} />
                    )}
                    {project?.title && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <PageTitle title={project.title} />
                            {project?.content_type?.name && (
                                <Chip
                                    label={project.content_type?.name
                                        .replace(/_/gi, " ")
                                        .split(" ")
                                        .map((word: string) => capitalize(word))
                                        .join(" ")}
                                    sx={PILL_SX}
                                />
                            )}
                            {(project as any)?.content_channels?.length > 0 &&
                                labelChannelEntries(
                                    (project as any).content_channels as any[]
                                ).map((ch: any, i: number) => (
                                    <Chip
                                        key={ch.brand_project_channel_id || `${ch.id}-${i}`}
                                        label={
                                            ch.internal_status
                                                ? `${ch.label}: ${ch.internal_status}`
                                                : ch.label
                                        }
                                        sx={CHANNEL_PILL_SX}
                                    />
                                ))}
                            {associationIds && (
                                <div
                                    className="bg-accent text-white font-bold text-xs rounded-md hover:bg-[#735C38] dark:hover:bg-accent-dark cursor-pointer p-1 px-2 shrink-0 whitespace-nowrap"
                                    title="Copy ID"
                                    onClick={handleIdClick()}
                                >
                                    {associationIds}
                                </div>
                            )}
                            {/* {project?.id && (
                                <div
                                    className="bg-accent text-white font-bold text-xs rounded-md hover:bg-[#735C38] dark:hover:bg-accent-dark cursor-pointer p-1 px-2 shrink-0 whitespace-nowrap"
                                    title="Copy ID"
                                    onClick={handleIdClick()}
                                >
                                    {formatProjectId(project)}
                                </div>
                            )} */}
                            {!isExpert(role) && project?.pricing_total != null && (
                                <PriceBreakdownTooltip
                                    breakdown={project.pricing_breakdown}
                                    pricingSnapshottedAt={project.pricing_snapshotted_at}
                                    project={project}
                                >
                                    <div
                                        className="flex items-center font-extrabold text-text-1 text-sm border border-border-token rounded-md px-2 py-1 shrink-0 whitespace-nowrap"
                                        style={{ cursor: project.pricing_breakdown ? "help" : "default" }}
                                    >
                                        {formatCurrency(project.pricing_total)}
                                    </div>
                                </PriceBreakdownTooltip>
                            )}
                        </div>
                    )}
                </div>
                {project && (
                    <div className="flex flex-row gap-2 px-[24px]">
                        <ProjectItemMenu
                            project={project}
                            hasExperts={projectPros.length > 0}
                            fetchProjects={() => {
                                window.location.reload();
                            }}
                            onUpdate={(updatedProject) => {
                                if (updatedProject) {
                                    setProject(updatedProject);
                                } else {
                                    fetchProject(true);
                                }
                                refreshAllData();
                            }}
                        />
                    </div>
                )}
            </div>

            {isProjectLoading && <DotLottleLoader />}
            {isInitializingFilters && <DotLottleLoader />}

            {project && !isProjectLoading && !isInitializingFilters && (
                <>
                    <div className="flex flex-wrap items-center px-6 gap-4">
                        <div className="flex flex-col items-start sm:flex-row sm:flex-wrap sm:items-center gap-x-3 gap-y-2 min-w-0">
                            <div className="flex items-center gap-[8px] min-w-0">
                                {(() => {
                                    const products = (project as any)?.products as
                                        | Array<{
                                              image?: string;
                                              title?: string;
                                          }>
                                        | undefined;
                                    const primaryProduct =
                                        products && products.length > 0 ? products[0] : project?.product;
                                    const productTitles =
                                        products && products.length > 0
                                            ? products
                                                  .map((p) => p.title)
                                                  .filter(Boolean)
                                                  .join(", ")
                                            : project?.product?.title || "";
                                    return (
                                        <>
                                            {/* image_placeholder.svg is black ink at
                                                30% on a transparent ground, so on the
                                                dark canvas it renders as nothing.
                                                Invert only the placeholder branch —
                                                real product photography is never
                                                touched (see the note in tokens.ts). */}
                                            <img
                                                className={`w-[20px] h-[20px] object-cover rounded-[6px] shrink-0${
                                                    primaryProduct?.image ? "" : " dark:invert"
                                                }`}
                                                src={primaryProduct?.image || imagePlaceholder}
                                                alt="Product"
                                            />
                                            <div className="overflow-hidden text-ellipsis whitespace-nowrap max-w-[300px] text-[14px] font-[450] text-text-2">
                                                {productTitles}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                            <BrandInlineList
                                brands={project?.brands?.sort((a, b) => (a.rmn ? -1 : 0) + (b.rmn ? 1 : 0))}
                                navigable
                            />

                            {getTopExpertDisplay(projectPros)}
                        </div>
                        <ExpertSelectionSwitcher
                            isActive={isActive}
                            setIsActive={handleTabChange}
                            videoCount={getVideoCounts(project).total}
                            videoTotal={getVideoCounts(project).deliverableTotal}
                            hasExperts={project?.experts !== false}
                        />
                    </div>

                    <Divider
                        style={{
                            marginLeft: "24px",
                            marginRight: "24px",
                            color: "var(--border)"
                        }}
                    />

                    {isActive === "expertProposals" && (
                        <div className="flex flex-col gap-[24px] pb-[40px]">
                            <div className="flex flex-wrap items-start gap-x-8 gap-y-4 sm:gap-[48px] px-[24px]">
                                <ExpertGeneralInfo
                                    title="Gender"
                                    description={project?.briefs?.[0]?.expert_gender || "N/A"}
                                />
                                <ExpertGeneralInfo
                                    title="Age Range"
                                    description={
                                        project?.briefs?.[0]?.expert_age_min && project?.briefs?.[0]?.expert_age_max
                                            ? `${project.briefs[0].expert_age_min}–${project.briefs[0].expert_age_max}`
                                            : "N/A"
                                    }
                                />
                                <ExpertGeneralInfo
                                    title="Profession / Expertise"
                                    description={getProfessionNames(project?.briefs?.[0])}
                                />
                                <IdealExpertExplanation
                                    products={algoProducts}
                                    idealsByDesireId={algoProductIdealsByDesireId}
                                    brandId={brandId}
                                />
                            </div>

                            {/* The band is correct in LIGHT (cream #F4F2EE behind
                                white cards reads as recessed) but inverts in DARK,
                                where elevation reads as lighter: --surface-2 #1F2A3B
                                is BRIGHTER than the --surface #18212F cards it holds,
                                so the cards punch holes in it, and the empty
                                AddExpertSelectCard slots (also --surface-2) vanish
                                into it entirely. Nothing in the dark ramp is darker
                                than --surface except --page, so the only correct dark
                                backdrop is no band at all. */}
                            <div className="bg-surface-2 dark:bg-transparent p-[24px]">
                                {pendingReopen && (
                                    <ReopenSelectionNotice
                                        expertName={pendingReopen.displayName}
                                        action={pendingReopen.action}
                                        siblingProjects={otherProcessProjects}
                                        busy={reopening}
                                        onConfirm={handleReopenConfirm}
                                        onCancel={() => setPendingReopen(null)}
                                    />
                                )}
                                {sharedProcess.isShared && (
                                    <Alert severity="info" className="mb-4">
                                        These experts are shared with{" "}
                                        {describeSharedSiblings(otherProcessProjects)}.
                                        Adding, removing, scoring, ranking, and submitting
                                        here updates every project on this selection process.
                                    </Alert>
                                )}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="text-text-1 text-[24px] font-black font-[CircularStd]">
                                        Selected ({projectProsCount})
                                        {sharedProcess.isShared && (
                                            <span className="ml-2 text-[13px] font-medium text-text-2 align-middle">
                                                · shared across {sharedProcess.siblingProjects.length} projects
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <FilmWindowField
                                            // The field seeds its picker from
                                            // this prop — key it so the
                                            // async-loaded (or re-saved) window
                                            // remounts it prefilled. The FRFD
                                            // mode is DERIVED from the saved
                                            // window AND its scheduled days, and
                                            // the exact option is gated on
                                            // film_days, so the key carries all
                                            // three.
                                            key={`${filmWindow?.est_film_start || ''}|${filmWindow?.est_film_end || ''}|${filmWindow?.film_days ?? ''}|${getFilmDays(filmWindow).join(',')}`}
                                            orderId={project?.order_id != null ? Number(project.order_id) : null}
                                            selectionId={selectionId}
                                            brandId={brandId ?? null}
                                            projectId={project?.id != null ? Number(project.id) : null}
                                            filmWindow={filmWindow}
                                            canEdit={canEditFilmWindow(role)}
                                            onSaved={(next, sideEffects) => {
                                                setFilmWindow(next);
                                                // Re-read the authoritative
                                                // film_dates for this selection
                                                // (GET /brand-orders/:id is the
                                                // only source), so the next
                                                // save's warning dialog and the
                                                // derived mode are computed from
                                                // what actually landed.
                                                refetchFilmWindow();
                                                if (sideEffects) {
                                                    fetchProject(true);
                                                    refreshAllData();
                                                }
                                            }}
                                            optedInCount={optedInExpertCount}
                                            rankedCount={(project as any)?.experts_ranked_count || 0}
                                            isCommitted={!!(project as any)?.process_locked}
                                            projectCount={(project as any)?.process_project_count || 1}
                                        />
                                        <Tooltip title={optInDisabledReason} arrow>
                                            <span className="inline-flex">
                                                <Button
                                                    onClick={() => setShowOptInModal(true)}
                                                    disabled={!!optInDisabledReason}
                                                    variant="outlined"
                                                    className={
                                                        optInDisabledReason
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : ""
                                                    }
                                                >
                                                    Send Opt In / Out Notifications
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={submitDisabledReason} arrow>
                                            <span className="inline-flex">
                                                <Button
                                                    onClick={handleSubmitButtonClick}
                                                    disabled={!!submitDisabledReason}
                                                    className={
                                                        submitDisabledReason
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : ""
                                                    }
                                                >
                                                    Submit For Review
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>

                                <Box className="overflow-auto w-full mt-4">
                                    <Box className="flex gap-4 min-w-fit [&>div]:w-[300px] [&>div]:shrink-0">
                                        {sortedProjectPros.map((expert, expertIndex) => (
                                            <AnimatedReorderItem key={expert.userid} orderKey={expertIndex}>
                                                <ExpertSelectionCard
                                                    isSelected
                                                    recommendationCheck={false}
                                                    pro={expert}
                                                    onUpdate={refreshAllData}
                                                    project={project}
                                                    onScoreChange={updateExpertScore}
                                                    onRateChange={updateExpertRate}
                                                    onRateSavingChange={handleRateSavingChange}
                                                    approvalCheck
                                                    isTopRecommendation={
                                                        Number(expert.projects?.[0]?.BrandProjectPro?.score) ===
                                                        maxPercentage
                                                    }
                                                    isApprovalPage={false}
                                                    algoProducts={algoProducts}
                                                    // Fall back to the score already loaded for this expert in the
                                                    // search pool so a just-added card shows it instantly (no "—"
                                                    // flash) until refetchAlgoScores() reconciles.
                                                    algoScores={
                                                        algoScoresByUserId[String(expert.userid)] ??
                                                        searchScoresByUserId[String(expert.userid)]
                                                    }
                                                    algoBreakdown={algoBreakdownByUserId[String(expert.userid)]}
                                                    algoIndustryScores={algoIndustryScoreByUserId[String(expert.userid)]}
                                                    algoExpertTypes={
                                                        algoExpertTypeByUserId[String(expert.userid)] ??
                                                        searchExpertTypesByUserId[String(expert.userid)]
                                                    }
                                                    algoProfessions={algoProfessionsByUserId[String(expert.userid)]}
                                                    sharedSiblingProjects={otherProcessProjects}
                                                    onRequestReopen={setPendingReopen}
                                                />
                                            </AnimatedReorderItem>
                                        ))}
                                        {[...Array(6)].map((_, index) => (
                                            <AddExpertSelectCard key={index} />
                                        ))}
                                    </Box>
                                </Box>
                            </div>

                            <div className="px-[24px]">
                                <ExpertSection
                                    pros={pros}
                                    project={project}
                                    onRequestReopen={setPendingReopen}
                                    fetchProjectPros={refreshAllData}
                                    projectPros={projectPros}
                                    onClearFilters={() => {
                                        setFilters({});
                                        setSearchText("");
                                        setHasEverClearedFilters(true);
                                    }}
                                    setFilters={setFilters}
                                    setSearchText={setSearchText}
                                    proCount={proCount}
                                    loading={loading}
                                    algoProducts={algoProducts}
                                    searchScoresByUserId={searchScoresByUserId}
                                    searchExpertTypesByUserId={searchExpertTypesByUserId}
                                    sortByScore={sortByScore}
                                    onToggleSortByScore={() => setSortByScore((v) => !v)}
                                />
                            </div>

                            {pros.length < proCount && <LoadMoreBtn onClick={handleLoadMore} loading={isLoadingMore} />}
                        </div>
                    )}

                    {isActive === "details" && (
                        <div className="px-[24px] flex flex-col gap-6 pb-10">
                            {project?.start_date && <ProjectTimelineCard project={project} />}
                            {!isExpert(role) && project?.id && (
                                <ActivityFeed
                                    level="project"
                                    id={Number(project.id)}
                                    filters="minimal"
                                    pollMs={60000}
                                />
                            )}
                            <div className="flex flex-col lg:flex-row gap-6">
                                <div className="flex-1 min-w-0">
                                    {project?.briefs?.[0] ? (
                                        <BriefView
                                            brief={project.briefs[0]}
                                            projectTitle={project?.title}
                                            showDownloadButton={project.briefs[0].status === "Brief Approved"}
                                        />
                                    ) : (
                                        <Box className="flex items-center justify-center my-10">
                                            <EmptyPanel
                                                showSpacing
                                                text="No brief available for this project"
                                                icon={<ProjectBriefIcon className="w-12 h-12" style={{ color: "var(--muted)" }} />}
                                                button={
                                                    <Button onClick={() => setShowBriefDialog(true)}>
                                                        Create Brief
                                                    </Button>
                                                }
                                            />
                                            {showBriefDialog && (
                                                <BriefDialog
                                                    project={project}
                                                    onClose={() => setShowBriefDialog(false)}
                                                    onComplete={(updatedProject) => {
                                                        setShowBriefDialog(false);
                                                        if (updatedProject) {
                                                            setProject(updatedProject);
                                                        } else {
                                                            fetchProject();
                                                        }
                                                        refreshAllData();
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </div>
                                {project && (
                                    <div className="w-full flex-1 min-w-0 flex flex-col gap-6">
                                        <ProductDetailsCard project={project} />
                                        {!isExpert(role) && <ProjectPricingCard project={project} />}
                                        <KeyProjectDatesCard project={project} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {isActive === "expertRanking" && (
                        <ExpertRanking
                            experts={projectPros}
                            projectId={projectId || ""}
                            brandId={brandId?.toString() || ""}
                            onUpdate={() => {
                                fetchProject(true);
                                fetchProjectPros();
                                refreshAllData();
                            }}
                            projectStatus={project?.status}
                            project={project}
                            role={role || undefined}
                            loading={projectProsLoading}
                            sharedProcessId={sharedProcess.isShared ? sharedProcess.processId : null}
                            sharedSiblingProjects={otherProcessProjects}
                        />
                    )}
                    {isActive === "downloads" && (
                        <UploadRegistryProvider value={uploadRegistry}>
                            <Downloads
                                project={project}
                                uploadUx
                                onProjectUpdate={refreshProjectFilesSilently}
                            />
                        </UploadRegistryProvider>
                    )}
                    <ConfirmationDialog
                        open={showConfirmDialog}
                        onClose={() => setShowConfirmDialog(false)}
                        onConfirm={handleSubmitForReview}
                        title="Expert Proposals"
                        description={
                            modalType === "alreadySubmitted"
                                ? "Experts have already been chosen for this project, do you wish to share updated proposals?"
                                : "Are you sure you want to submit these experts for review?"
                        }
                        confirmLabel={modalType === "alreadySubmitted" ? "Share" : "Submit"}
                        cancelLabel="Cancel"
                    />

                </>
            )}

            {searchParams.get("expertSelection") && (
                <ExpertSelectionRecommendationDialog
                    onConfirm={() => {
                        fetchProject(true);
                        refreshAllData();
                    }}
                />
            )}
            <OptInNotificationsModal
                open={showOptInModal}
                onClose={() => setShowOptInModal(false)}
                pros={projectPros}
                brandId={brandId}
                projectId={project?.id ? Number(project.id) : undefined}
                onSent={refreshAllData}
                sharedSiblingProjects={otherProcessProjects}
            />
            {searchParams.get("edit") && project && (
                <AddProjectModal
                    project={project}
                    isEdit
                    setOpen={(open: boolean) => {
                        if (!open)
                            setSearchParams(
                                (p) => {
                                    p.delete("edit");
                                    return p;
                                },
                                { replace: true }
                            );
                    }}
                    fetchProjects={() => {
                        setSearchParams(
                            (p) => {
                                p.delete("edit");
                                return p;
                            },
                            { replace: true }
                        );
                        fetchProject(true);
                        refreshAllData();
                    }}
                />
            )}
            {briefNoteLink && project && briefNoteProjectReady && !briefNoteBlocked && (
                <BriefDialog
                    key={project.id}
                    project={project}
                    focusNoteId={briefNoteLink.noteId}
                    onClose={closeBriefNoteLink}
                    onComplete={(updatedProject) => {
                        closeBriefNoteLink();
                        if (updatedProject) {
                            setProject(updatedProject);
                        } else {
                            fetchProject(true);
                        }
                        refreshAllData();
                    }}
                />
            )}
        </div>
    );
};

export default ExpertSelection;
